import React, { useState, useContext, useMemo, useEffect } from "react";
import "./contact.css";
import { MyContext } from "../../Context/MyContext";
import API from "../../Api/api";
import { toast } from "react-toastify";

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .9h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.75a16 16 0 006.29 6.29l1.13-1.16a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="17" height="17">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const INFO_ITEMS = [
  { key: "phone",   label: "Call Us",  Icon: IconPhone },
  { key: "email",   label: "Email Us", Icon: IconMail  },
  { key: "address", label: "Visit Us", Icon: IconPin   },
];

const ContactUs = () => {
  const { contact } = useContext(MyContext);
  const {
    title,
    description,
    heroHeading,
    heroSub,
    heroAvailability,
    officeLabel,
    tagText,
    successMessage,
    sendText,
    sentText,
    infoItems,
    stats,
    formFields,
    contactInfo,
    image,
  } = contact || {};

  const [focused, setFocused] = useState(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});

  const safeFormFields = useMemo(() => (Array.isArray(formFields) ? formFields : []), [formFields]);

  useEffect(() => {
    const init = {};
    safeFormFields.forEach((f) => {
      const key = f.name || String(f.id);
      init[key] = "";
    });
    setFormValues(init);
  }, [safeFormFields]);

  const handleChange = (key, val) => {
    setFormValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (formValues.name || "").trim();
    const phone = (formValues.phone || "").trim();
    const email = (formValues.email || "").trim();
    const message = [formValues.subject, formValues.message].filter(Boolean).join(" — ").trim() || (formValues.message || "").trim();
    if (!name) {
      toast.error("Please enter your name.");
      return;
    }
    if (!phone && !email) {
      toast.error("Please enter a phone number or email.");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/contact-leads", {
        name,
        phone: phone || "",
        email: email || "",
        message: message || "(no message)",
        source: "web",
      });
      setSent(true);
      toast.success(successMessage || "Message sent!");
      setFormValues((prev) => {
        const cleared = { ...prev };
        Object.keys(cleared).forEach((k) => {
          cleared[k] = "";
        });
        return cleared;
      });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ct-root">

      {/* ══ HERO ══ */}
      <div className="ct-hero">
        <img src={image} alt="Luxury property" className="ct-hero-img" />
        <div className="ct-hero-grad" />

        <div className="ct-hero-content">
          <div className="ct-hero-pill">
            <span className="ct-pill-dot" />
            {heroAvailability}
          </div>
          <h1
            className="ct-hero-h1"
            dangerouslySetInnerHTML={{
              __html: heroHeading.replace("Perfect Space", "<em>Perfect Space</em>"),
            }}
          />
          <p className="ct-hero-sub">{heroSub}</p>
        </div>

        <div className="ct-hero-stats">
          {stats.map((s) => (
            <div className="ct-stat" key={s.label}>
              <span className="ct-stat-num">{s.value}</span>
              <span className="ct-stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ══ BOTTOM ══ */}
      <div className="ct-bottom">

        {/* Form card */}
        <div className="ct-form-card">
          <span className="ct-tag">{tagText}</span>
          <h2 className="ct-form-title">{title}</h2>
          <p className="ct-form-desc">{description}</p>

          <form onSubmit={handleSubmit}>
            <div className="ct-field-grid">
              {safeFormFields.map((field) => {
                const key = field.name || String(field.id);
                return field.type === "textarea" ? (
                  <div
                    key={field.id}
                    className={`ct-field ct-field--full${focused === field.id ? " ct-field--focus" : ""}`}
                  >
                    <label>{field.label}</label>
                    <textarea
                      placeholder={field.placeholder}
                      value={formValues[key] ?? ""}
                      onChange={(ev) => handleChange(key, ev.target.value)}
                      onFocus={() => setFocused(field.id)}
                      onBlur={() => setFocused(null)}
                    />
                  </div>
                ) : (
                  <div
                    key={field.id}
                    className={`ct-field${focused === field.id ? " ct-field--focus" : ""}`}
                  >
                    <label>{field.label}</label>
                    <input
                      type={field.name === "email" ? "email" : field.name === "phone" ? "tel" : "text"}
                      placeholder={field.placeholder}
                      value={formValues[key] ?? ""}
                      onChange={(ev) => handleChange(key, ev.target.value)}
                      onFocus={() => setFocused(field.id)}
                      onBlur={() => setFocused(null)}
                    />
                  </div>
                );
              })}
            </div>

           <button
  type="submit"
  disabled={submitting}
  className={`ct-send-btn${sent ? " ct-send-btn--sent" : ""}`}
>
  {sent ? <><IconCheck /> {sentText}</> : submitting ? "Sending…" : <><IconSend /> {sendText}</>}
</button>
          </form>

          {sent && (
            <div className="ct-success">
              <IconCheck /> {successMessage}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="ct-sidebar">
          <div className="ct-map">
            <div className="ct-map-chip"><IconPin /> {officeLabel}</div>
            <iframe
              title="map"
              src="https://maps.google.com/maps?q=Madurai&t=&z=13&ie=UTF8&iwloc=&output=embed"
              loading="lazy"
            />
          </div>

          <div className="ct-info-list">
          {infoItems.map(({ key, label }) => {
  const Icon =
    key === "phone" ? IconPhone :
    key === "email" ? IconMail :
    IconPin;

  return (
    <div className="ct-info-card" key={key}>
      <div className="ct-info-icon"><Icon /></div>
      <div>
        <span className="ct-info-lbl">{label}</span>
        <p className="ct-info-val">{contactInfo[key]}</p>
      </div>
    </div>
  );
})}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ContactUs;